import React, { useEffect, useState, useMemo } from "react";
import {
  Container, Row, Col, Card, CardBody, Badge, Spinner, Input, Button,
} from "reactstrap";
import { Link } from "react-router-dom";
import { getAllPublicListings } from "../../helpers/firestoreService";
import { getListingImageUrl } from "../../helpers/storageService";
import SeoMeta from "../../components/Common/SeoMeta";
import { logAnalyticsEvent } from "../../firebaseConfig";

const BEDS_OPTIONS = ["Any", "Studio", "1", "2", "3", "4+"];
const PET_OPTIONS  = ["Any", "Allowed", "Not Allowed", "Negotiable"];
const FILTER_THRESHOLD = 10;

const ListingsGallery = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [thumbnailUrls, setThumbnailUrls] = useState({});

  // Filters
  const [search, setSearch]     = useState("");
  const [maxRent, setMaxRent]   = useState("");
  const [beds, setBeds]         = useState("Any");
  const [pets, setPets]         = useState("Any");

  useEffect(() => {
    (async () => {
      try {
        setListings(await getAllPublicListings());
      } catch (e) {
        console.error("ListingsGallery load error:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const withThumbnails = listings.filter((listing) => listing.thumbnailKey);
    if (!withThumbnails.length) {
      setThumbnailUrls({});
      return undefined;
    }
    Promise.all(withThumbnails.map(async (listing) => [
      listing.id,
      await getListingImageUrl(listing.thumbnailKey).catch(() => ""),
    ])).then((entries) => {
      if (!cancelled) setThumbnailUrls(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [listings]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const maxR = parseFloat(maxRent) || Infinity;
    return listings.filter((l) => {
      if (q && !(
        (l.title || "").toLowerCase().includes(q) ||
        (l.city || "").toLowerCase().includes(q) ||
        (l.stateCode || "").toLowerCase().includes(q) ||
        (l.neighborhood || "").toLowerCase().includes(q)
      )) return false;
      if (maxR < Infinity && (l.monthlyRent || 0) > maxR) return false;
      if (beds !== "Any") {
        if (beds === "Studio" && l.bedrooms !== "0" && l.bedrooms !== 0) return false;
        if (beds === "4+" && parseInt(l.bedrooms) < 4) return false;
        if (!["Studio", "4+"].includes(beds) && String(l.bedrooms) !== beds) return false;
      }
      if (pets !== "Any") {
        const lp = (l.petPolicy || "").toLowerCase();
        if (pets === "Allowed" && !lp.includes("allow")) return false;
        if (pets === "Not Allowed" && (lp.includes("allow") || lp === "")) return false;
        if (pets === "Negotiable" && !lp.includes("negot")) return false;
      }
      return true;
    });
  }, [listings, search, maxRent, beds, pets]);

  const showFilters = listings.length >= FILTER_THRESHOLD;
  const listingCountLabel = loading
    ? "Loading available homes..."
    : `${filtered.length} listing${filtered.length !== 1 ? "s" : ""} available`;

  return (
    <div className="page-content">
      <SeoMeta
        title="Available Rentals | Porcher"
        description="View homes currently available from landlords and property managers using Porcher."
        canonicalPath="/rentals"
      />
      <Container fluid>
        {/* Header */}
        <div className="mb-4">
          <h3 className="fw-bold mb-1">Available homes managed through Porcher</h3>
          <p className="text-muted mb-2">{listingCountLabel}</p>
          <p className="text-muted mb-0" style={{ maxWidth: 760 }}>
            Porcher listings are created by landlords and property managers who use Porcher
            to receive inquiries, process applications, screen tenants, create leases, and collect rent.
            More homes will be added as they become available.
          </p>
        </div>

        <Row className="g-3 mb-4">
          {[
            ["bx bx-user-check", "Landlord-created listings", "Each available home is published by the landlord or property manager responsible for the rental."],
            ["bx bx-lock-alt", "Secure application flow", "Applications and sensitive details are handled through Porcher instead of email threads or paper forms."],
            ["bx bx-file", "Screening disclosed up front", "Application fees, screening steps, and policy links are shown before renters submit."],
          ].map(([icon, title, copy]) => (
            <Col md={4} key={title}>
              <Card className="border-0 shadow-sm h-100">
                <CardBody className="p-3 d-flex gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 40, height: 40, background: "#eff2ff", color: "#556ee6" }}
                  >
                    <i className={icon} />
                  </div>
                  <div>
                    <div className="fw-semibold small mb-1">{title}</div>
                    <p className="text-muted small mb-0">{copy}</p>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Filter bar */}
        {showFilters && (
          <Card className="border-0 shadow-sm mb-4">
            <CardBody className="py-3">
              <Row className="g-2 align-items-end">
                <Col xs={12} md={4}>
                  <Input
                    placeholder="Search by name or address…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    bsSize="sm"
                  />
                </Col>
                <Col xs={6} md={2}>
                  <Input
                    type="number"
                    placeholder="Max rent $"
                    value={maxRent}
                    onChange={(e) => setMaxRent(e.target.value)}
                    min={0}
                    bsSize="sm"
                  />
                </Col>
                <Col xs={6} md={2}>
                  <Input type="select" value={beds} onChange={(e) => setBeds(e.target.value)} bsSize="sm">
                    {BEDS_OPTIONS.map((o) => <option key={o}>{o === "Any" ? "Bedrooms" : `${o} bed${o === "1" ? "" : "s"}`}</option>)}
                  </Input>
                </Col>
                <Col xs={6} md={2}>
                  <Input type="select" value={pets} onChange={(e) => setPets(e.target.value)} bsSize="sm">
                    {PET_OPTIONS.map((o) => <option key={o}>{o === "Any" ? "Pets" : o}</option>)}
                  </Input>
                </Col>
                <Col xs={6} md={2}>
                  <Button color="light" size="sm" block onClick={() => { setSearch(""); setMaxRent(""); setBeds("Any"); setPets("Any"); }}>
                    Clear
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}

        {/* Results */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : filtered.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardBody className="py-5 text-center">
              <i className="bx bx-home-alt text-muted" style={{ fontSize: "3rem" }} />
              <h5 className="mt-3 mb-2">
                {showFilters ? "No listings match your filters" : "No homes are available right now"}
              </h5>
              <p className="text-muted mb-0">
                {showFilters ? "Try adjusting your search criteria." : "More homes will be added as they become available."}
              </p>
            </CardBody>
          </Card>
        ) : (
          <Row>
            {filtered.map((l) => (
              <Col key={l.id} xl={3} lg={4} md={6} className="mb-4">
                <Card
                  tag={Link}
                  to={`/rentals/${l.id}`}
                  className="border-0 shadow-sm h-100"
                  style={{ cursor: "pointer", transition: "box-shadow 0.15s", color: "inherit", textDecoration: "none" }}
                  onClick={() => logAnalyticsEvent("rental_listing_click", {
                    listing_position: filtered.findIndex((item) => item.id === l.id) + 1,
                    inventory_count: listings.length,
                  })}
                  onMouseEnter={(e) => e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.12)"}
                  onMouseLeave={(e) => e.currentTarget.style.boxShadow = ""}
                >
                  {/* Hero image */}
                  <div style={{ height: 180, background: "#f1f5f9", overflow: "hidden", borderRadius: "8px 8px 0 0", flexShrink: 0 }}>
                    {thumbnailUrls[l.id] ? (
                      <img
                        src={thumbnailUrls[l.id]}
                        alt={l.title}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      <div className="d-flex align-items-center justify-content-center h-100">
                        <i className="bx bx-building-house text-muted" style={{ fontSize: "2.5rem" }} />
                      </div>
                    )}
                  </div>

                  <CardBody className="p-3">
                    <h6 className="fw-semibold mb-1 text-truncate">{l.title || "Rental Unit"}</h6>
                    {(l.neighborhood || l.city || l.stateCode) && (
                      <p className="text-muted small mb-2 text-truncate">
                        <i className="bx bx-map me-1" />
                        {[l.neighborhood, l.city, l.stateCode].filter(Boolean).join(", ")}
                      </p>
                    )}

                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <span className="fw-bold" style={{ color: "#556ee6", fontSize: "1.05rem" }}>
                        {l.monthlyRent ? `$${Number(l.monthlyRent).toLocaleString()}/mo` : "Price TBD"}
                      </span>
                      <div className="d-flex gap-1">
                        {l.bedrooms !== undefined && l.bedrooms !== "" && (
                          <Badge color="light" className="text-dark fw-normal small">
                            {l.bedrooms === "0" || l.bedrooms === 0 ? "Studio" : `${l.bedrooms} bd`}
                          </Badge>
                        )}
                        {l.bathrooms !== undefined && l.bathrooms !== "" && (
                          <Badge color="light" className="text-dark fw-normal small">{l.bathrooms} ba</Badge>
                        )}
                        {l.sqft && (
                          <Badge color="light" className="text-dark fw-normal small">{Number(l.sqft).toLocaleString()} ft²</Badge>
                        )}
                      </div>
                    </div>

                    <div className="d-flex flex-wrap gap-1 mt-auto">
                      {l.petPolicy && l.petPolicy !== "none" && (
                        <span className="badge rounded-pill" style={{ background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0", fontSize: "0.7rem" }}>
                          <i className="bx bx-paw me-1" />{l.petPolicy}
                        </span>
                      )}
                      {l.parkingIncluded && (
                        <span className="badge rounded-pill" style={{ background: "#eff2ff", color: "#556ee6", border: "1px solid #c7d2fe", fontSize: "0.7rem" }}>
                          <i className="bx bx-car me-1" />Parking
                        </span>
                      )}
                      {l.availableDate && (
                        <span className="badge rounded-pill" style={{ background: "#fefce8", color: "#a16207", border: "1px solid #fde68a", fontSize: "0.7rem" }}>
                          <i className="bx bx-calendar me-1" />
                          {new Date(l.availableDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        </span>
                      )}
                    </div>
                    <div className="border-top mt-3 pt-2">
                      <span className="text-muted small d-inline-flex align-items-center">
                        <i className="bx bx-check-shield me-1" /> Powered by Porcher secure applications
                      </span>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Container>
    </div>
  );
};

export default ListingsGallery;
